import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Briefcase } from "lucide-react";
import { toast } from "sonner";

export default function Empresas() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    nome: "", cnpj: "", telefone: "", email: "", endereco: "", responsavel: "",
  });

  const load = async () => {
    const { data } = await api.get("/empresas");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.error("Informe o nome da empresa");
    try {
      await api.post("/empresas", form);
      toast.success("Empresa cadastrada");
      setOpen(false);
      setForm({ nome: "", cnpj: "", telefone: "", email: "", endereco: "", responsavel: "" });
      load();
    } catch { toast.error("Erro ao cadastrar empresa"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta empresa? Hóspedes e reservas ligados serão desvinculados.")) return;
    try {
      await api.delete(`/empresas/${id}`);
      toast.success("Empresa removida");
      load();
    } catch { toast.error("Erro ao remover"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Contratantes corporativos"
        title="Empresas"
        description="Cadastre empresas que contratam quartos e vincule-as aos seus hóspedes."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-nova-empresa" className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                <Plus size={16} className="mr-2" /> Nova empresa
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif text-2xl">Nova empresa</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Razão social / Nome</Label>
                  <Input data-testid="input-empresa-nome" value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>CNPJ</Label>
                    <Input data-testid="input-empresa-cnpj" value={form.cnpj}
                      onChange={(e) => setForm({ ...form, cnpj: e.target.value })} />
                  </div>
                  <div>
                    <Label>Responsável</Label>
                    <Input data-testid="input-empresa-responsavel" value={form.responsavel}
                      onChange={(e) => setForm({ ...form, responsavel: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Telefone</Label>
                    <Input data-testid="input-empresa-telefone" value={form.telefone}
                      onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" data-testid="input-empresa-email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Endereço</Label>
                  <Input data-testid="input-empresa-endereco" value={form.endereco}
                    onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="btn-submit-empresa" className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                    Cadastrar empresa
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((e) => (
          <div
            key={e.id}
            data-testid={`empresa-card-${e.id}`}
            className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-6 hover:border-[#083A6B] transition-colors"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 rounded-md bg-[#EAF0EA] flex items-center justify-center text-[#083A6B]">
                <Briefcase size={20} strokeWidth={1.6} />
              </div>
              <button
                data-testid={`btn-delete-empresa-${e.id}`}
                onClick={() => remove(e.id)}
                className="text-stone-400 hover:text-[#D4A652] p-1"
                aria-label="Excluir"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div className="font-serif text-2xl text-stone-900 leading-tight mb-2">{e.nome}</div>
            {e.cnpj && <div className="text-xs uppercase tracking-widest text-[#D4A652] mb-1">CNPJ {e.cnpj}</div>}
            <div className="text-sm text-stone-500">{e.responsavel ? `Responsável: ${e.responsavel}` : "—"}</div>
            <div className="text-sm text-stone-500">{e.telefone || "—"}  ·  {e.email || "—"}</div>
            {e.endereco && <div className="text-xs text-stone-500 mt-2">{e.endereco}</div>}
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center py-16 text-stone-500 bg-[#FFFFFF] border border-dashed border-stone-200 rounded-lg">
            Nenhuma empresa cadastrada ainda. Comece adicionando os contratantes corporativos.
          </div>
        )}
      </div>
    </div>
  );
}
